import { useState, useMemo, useEffect } from 'react'
import { useImporterViewModel } from '../ImporterViewModel'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { ColumnDef } from '@tanstack/react-table'
import { FbLink } from 'components/FbUI/FbLink'
import FbButton from 'components/FbUI/FbButton'
import { getCompanyPageUrl } from 'utils/oppTypesMap'
import { OperationType, Operation } from './types'
import { OppTypeBadge } from './OppTypeBadge'
import { useContainerHeight } from './useContainerHeight'

type ImportTab = 'custom' | 'tags' | 'deals' | 'contacts' | 'notes'

interface DetailedImportViewProps {
  show: boolean
  viewModel: ReturnType<typeof useImporterViewModel>
  onImportTags: () => void
  onImportDeals: () => void
  onImportContacts: () => void
  onImportNotes: () => void
  isProcessing: boolean
  currentOperation: Operation | null
}

/** Config for each tab: label, whether it has an import button, and the operation type */
const TAB_CONFIG: Record<
  ImportTab,
  {
    label: string
    importLabel?: string
    operationType?: OperationType
  }
> = {
  custom: { label: 'Custom' },
  tags: {
    label: 'Tags',
    importLabel: 'Import Tags',
    operationType: OperationType.SET_TAGS,
  },
  deals: {
    label: 'Deals',
    importLabel: 'Import Deals',
    operationType: OperationType.SET_DEALS,
  },
  contacts: {
    label: 'Contacts',
    importLabel: 'Import Contacts',
    operationType: OperationType.SET_CONTACTS,
  },
  notes: {
    label: 'Notes',
    importLabel: 'Import Notes',
    operationType: OperationType.SET_NOTES,
  },
}

export function DetailedImportView({
  show,
  viewModel,
  onImportTags,
  onImportDeals,
  onImportContacts,
  onImportNotes,
  isProcessing,
  currentOperation,
}: DetailedImportViewProps) {
  const c = viewModel.importCounts
  const [tableContainerRef, tableContainerHeight] = useContainerHeight()

  // Only show tabs that have data (total > 0)
  const visibleTabs = useMemo(() => {
    const allTabs: ImportTab[] = [
      'custom',
      'tags',
      'deals',
      'contacts',
      'notes',
    ]
    return allTabs.filter((key) => c[key].total > 0)
  }, [c])

  const [activeTab, setActiveTab] = useState<ImportTab | null>(null)

  // Set initial tab to first visible tab, and reset if current tab becomes hidden.
  // Uses functional setState to avoid activeTab in the dependency array.
  useEffect(() => {
    if (visibleTabs.length === 0) {
      setActiveTab(null)
      return
    }
    setActiveTab((prev) => {
      if (!prev || !visibleTabs.includes(prev)) {
        return visibleTabs[0]
      }
      return prev
    })
  }, [visibleTabs])

  // Import handler map
  const importHandlers: Partial<Record<ImportTab, () => void>> = useMemo(
    () => ({
      tags: onImportTags,
      deals: onImportDeals,
      contacts: onImportContacts,
      notes: onImportNotes,
    }),
    [onImportTags, onImportDeals, onImportContacts, onImportNotes]
  )

  // Status column shared across all tables
  const statusColumn: ColumnDef<any> = useMemo(
    () => ({
      header: 'Status',
      accessorKey: 'isImported',
      cell: ({ row }: any) => {
        const isImported = row.original.isImported
        return (
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              isImported
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {isImported ? 'Imported' : 'Pending'}
          </span>
        )
      },
    }),
    []
  )

  // Company column shared across all tables
  const companyColumn: ColumnDef<any> = useMemo(
    () => ({
      header: 'Company',
      accessorKey: 'companyName',
      cell: ({ row }: any) => {
        const { companyName, isCustomCompany, firstbiteId } = row.original

        if (!firstbiteId) {
          return (
            <div className="font-semibold text-gray-900">
              {companyName}
              {isCustomCompany && (
                <span className="ml-2 text-xs text-gray-500">(Custom)</span>
              )}
            </div>
          )
        }

        const url = getCompanyPageUrl(firstbiteId)

        return (
          <div className="font-semibold">
            <FbLink to={url}>{companyName}</FbLink>
            {isCustomCompany && (
              <span className="ml-2 text-xs text-gray-500">(Custom)</span>
            )}
          </div>
        )
      },
    }),
    []
  )

  // Type column shared across all tables (for sorting by company type)
  const typeColumn: ColumnDef<any> = useMemo(
    () => ({
      header: 'Company Type',
      accessorKey: 'companyTargetType',
      cell: ({ row }: any) => {
        const { companyTargetType } = row.original
        if (!companyTargetType) return '-'
        return <OppTypeBadge customLabel={companyTargetType} />
      },
    }),
    []
  )

  // Get unique input field keys across all custom companies for dynamic columns
  const customInputFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const item of viewModel.detailedImportData.custom) {
      if (item.inputFields) {
        for (const key of Object.keys(item.inputFields)) {
          keys.add(key)
        }
      }
    }
    return Array.from(keys)
  }, [viewModel.detailedImportData.custom])

  // Define columns for each tab
  const customColumns: ColumnDef<any>[] = useMemo(() => {
    const cols: ColumnDef<any>[] = [statusColumn, typeColumn]

    // Add dynamic columns for each input field (excluding Company Name since it's in the Company column)
    for (const fieldKey of customInputFieldKeys) {
      if (fieldKey === 'Company Name') continue // Skip - already shown in Company column
      cols.push({
        header: fieldKey,
        accessorKey: `inputFields.${fieldKey}`,
        cell: ({ row }) => {
          const value = row.original.inputFields?.[fieldKey]
          return value || '-'
        },
      })
    }

    return cols
  }, [statusColumn, typeColumn, customInputFieldKeys])

  const tagsColumns: ColumnDef<any>[] = useMemo(
    () => [
      statusColumn,
      companyColumn,
      typeColumn,
      {
        header: 'Tag Name',
        accessorKey: 'tag_name',
        cell: ({ row }: any) => (
          <span className="px-2 py-1 bg-surface-hover text-fb-dark-green text-xs rounded">
            {row.original.tag_name}
          </span>
        ),
      },
    ],
    [statusColumn, companyColumn, typeColumn]
  )

  const dealsColumns: ColumnDef<any>[] = useMemo(
    () => [
      statusColumn,
      companyColumn,
      typeColumn,
      {
        header: 'Sales Stage',
        accessorKey: 'deal_sales_stage',
        cell: ({ row }: any) => row.original.deal_sales_stage || '-',
      },
      {
        header: 'Account Owner',
        accessorKey: 'deal_account_owner',
        cell: ({ row }: any) => row.original.deal_account_owner || '-',
      },
      {
        header: 'Product',
        accessorKey: 'deal_product',
        cell: ({ row }: any) => row.original.deal_product || '-',
      },
    ],
    [statusColumn, companyColumn, typeColumn]
  )

  const contactsColumns: ColumnDef<any>[] = useMemo(
    () => [
      statusColumn,
      companyColumn,
      typeColumn,
      {
        header: 'Name',
        accessorKey: 'contact_full_name',
        cell: ({ row }: any) => {
          const fullName =
            row.original.contact_full_name ||
            `${row.original.contact_first_name || ''} ${row.original.contact_last_name || ''}`.trim()
          return fullName || '-'
        },
      },
      {
        header: 'Title',
        accessorKey: 'contact_title',
        cell: ({ row }: any) => row.original.contact_title || '-',
      },
      {
        header: 'Email',
        accessorKey: 'contact_work_email',
        cell: ({ row }: any) => {
          const email =
            row.original.contact_work_email ||
            row.original.contact_personal_email ||
            row.original.contact_generic_email
          return email || '-'
        },
      },
      {
        header: 'Phone',
        accessorKey: 'contact_phone',
        cell: ({ row }: any) => row.original.contact_phone || '-',
      },
      {
        header: 'Label',
        accessorKey: 'contact_label',
        cell: ({ row }: any) => row.original.contact_label || '-',
      },
      {
        header: 'Skip Reason',
        accessorKey: 'failed_import_reason',
        cell: ({ row }: any) => {
          const reason = row.original.failed_import_reason
          if (!reason) return '-'
          return (
            <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">
              {reason}
            </span>
          )
        },
      },
    ],
    [statusColumn, companyColumn, typeColumn]
  )

  const notesColumns: ColumnDef<any>[] = useMemo(
    () => [
      statusColumn,
      companyColumn,
      typeColumn,
      {
        header: 'Note',
        accessorKey: 'company_note',
        cell: ({ row }: any) => (
          <span className="text-gray-700">{row.original.company_note}</span>
        ),
      },
    ],
    [statusColumn, companyColumn, typeColumn]
  )

  // Get data and columns for active tab
  const { data, columns } = useMemo(() => {
    switch (activeTab) {
      case 'custom':
        return {
          data: viewModel.detailedImportData.custom,
          columns: customColumns,
        }
      case 'tags':
        return { data: viewModel.detailedImportData.tags, columns: tagsColumns }
      case 'deals':
        return {
          data: viewModel.detailedImportData.deals,
          columns: dealsColumns,
        }
      case 'contacts':
        return {
          data: viewModel.detailedImportData.contacts,
          columns: contactsColumns,
        }
      case 'notes':
        return {
          data: viewModel.detailedImportData.notes,
          columns: notesColumns,
        }
      default:
        return { data: [], columns: [] as ColumnDef<any>[] }
    }
  }, [
    activeTab,
    viewModel.detailedImportData,
    customColumns,
    tagsColumns,
    dealsColumns,
    contactsColumns,
    notesColumns,
  ])

  if (!show) {
    return null
  }

  if (visibleTabs.length === 0 || !activeTab) {
    return (
      <div className="text-center text-gray-500 py-12">No data to import.</div>
    )
  }

  const activeConfig = TAB_CONFIG[activeTab]
  const activeCounts = c[activeTab]
  const activeImportHandler = importHandlers[activeTab]

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar with import button on the right */}
      <div className="border-b border-gray-200 mb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {visibleTabs.map((tabKey) => {
              const config = TAB_CONFIG[tabKey]
              const counts = c[tabKey]
              const isActive = activeTab === tabKey

              return (
                <button
                  key={tabKey}
                  onClick={() => setActiveTab(tabKey)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    isActive
                      ? 'border-fb-dark-green text-fb-dark-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {config.label}{' '}
                  <span
                    className={
                      counts.imported === counts.total
                        ? 'text-green-600'
                        : 'opacity-60'
                    }
                  >
                    {counts.imported}/{counts.total}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Import button (for importable tabs) */}
          {activeConfig.importLabel && activeImportHandler && (
            <FbButton
              size="sm"
              variant="primary"
              onClick={() => {
                activeImportHandler()
              }}
              loading={
                isProcessing &&
                currentOperation?.type === activeConfig.operationType
              }
              disabled={
                activeCounts.imported === activeCounts.total ||
                activeCounts.total === 0
              }
            >
              {activeConfig.importLabel}
            </FbButton>
          )}
        </div>
      </div>

      <div ref={tableContainerRef} className="flex-1 min-h-0">
        <ClientDataTable
          height={tableContainerHeight || 400}
          data={data}
          columns={columns}
          enableSorting
        />
      </div>
    </div>
  )
}
